import { useEffect, useRef, useState } from 'react';
import { useLang, localName } from '../../lib/lang';
import type { Status } from '../../lib/types';

/**
 * Colored status picker — a custom dropdown (native <option> can't reliably
 * show colors across browsers). Each option shows a colored dot + the status
 * name in its own colour; the closed control shows the selected status coloured.
 * Names are localized (Arabic name_ar with English fallback).
 */
export function StatusSelect({
  statuses, value, onChange, placeholder,
}: {
  statuses: Status[];
  value: number | null;
  onChange: (id: number) => void;
  placeholder?: string;
}): JSX.Element {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const current = statuses.find((s) => s.id === value) ?? null;

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className="appt-input flex items-center justify-between gap-2 text-start">
        <span className="flex min-w-0 items-center gap-2">
          <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: current?.color ?? '#cbd5e1' }} />
          <span className="truncate font-medium" style={{ color: current?.color ?? undefined }}>
            {current ? localName(lang, current.name, current.name_ar) : placeholder ?? '—'}
          </span>
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-none text-slate-400">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg appt-scroll dark:border-slate-700 dark:bg-slate-800">
          {statuses.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => { onChange(s.id); setOpen(false); }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-start text-base hover:bg-slate-50 dark:hover:bg-slate-700 ${s.id === value ? 'bg-slate-50 dark:bg-slate-700/60' : ''}`}
            >
              <span className="h-2.5 w-2.5 flex-none rounded-full" style={{ background: s.color }} />
              <span className="truncate font-medium" style={{ color: s.color }}>{localName(lang, s.name, s.name_ar)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
